'use client';

import { useState } from 'react';
import { ShoppingCart } from 'lucide-react';

import {
  ArticleDetailDialog,
  CartDrawer,
  CartProvider,
} from '@kit/shops/components';
import type { ArticleLike } from '@kit/shops/components';
import { useCartContext } from '@kit/shops/hooks';
import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import type { Database } from '~/lib/database.types';
import { formatCurrency } from '~/lib/formatters';
import { ArticleCard } from './article-card';

type ArticleRow = Database['public']['Tables']['shop_articles']['Row'];
type ShopRow = Database['public']['Tables']['shops']['Row'];

interface ShopViewProps {
  shop: ShopRow;
  articles: ArticleRow[];
}

/** Inner component that consumes CartContext (must be inside CartProvider) */
function ShopViewInner({ shop, articles }: ShopViewProps) {
  const [selectedArticle, setSelectedArticle] = useState<ArticleRow | null>(
    null,
  );
  const [cartOpen, setCartOpen] = useState(false);

  const currency = shop.default_currency ?? 'EUR';
  const { totalItems } = useCartContext();

  function handleBuy(article: ArticleRow) {
    // External products open in a new tab. Route through the tracked
    // /r/<redirect_code> redirect for attribution when a code is present; fall
    // back to the raw external_url only when it is not.
    if (article.external_url) {
      const target = article.redirect_code
        ? `/r/${article.redirect_code}`
        : article.external_url;
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    // For non-external items, "Buy now" from the detail dialog adds to cart
    // and opens the cart drawer
    setSelectedArticle(null);
    setCartOpen(true);
  }

  function handleCardClick(article: ArticleRow) {
    setSelectedArticle(article);
  }

  function handleDetailBuy(article: ArticleLike) {
    handleBuy(article as ArticleRow);
  }

  if (articles.length === 0) {
    return (
      <div className="py-20 text-center">
        <p className="text-muted-foreground text-lg">
          <Trans
            i18nKey="shop:noArticles"
            defaults="No products available yet."
          />
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Cart icon header */}
      <div className="mb-6 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="relative gap-2"
          onClick={() => setCartOpen(true)}
        >
          <ShoppingCart className="h-4 w-4" />
          <Trans i18nKey="shop:cart" defaults="Cart" />
          {totalItems > 0 && (
            <span className="bg-primary text-primary-foreground absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold">
              {totalItems > 99 ? '99+' : totalItems}
            </span>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {articles.map((article) => (
          <ArticleCard
            key={article.id}
            article={article}
            currency={currency}
            onBuy={() => handleBuy(article)}
            onCardClick={() => handleCardClick(article)}
          />
        ))}
      </div>

      {/* Article detail dialog */}
      <ArticleDetailDialog
        article={selectedArticle}
        currency={currency}
        isOpen={selectedArticle !== null}
        isPurchasePending={false}
        onClose={() => setSelectedArticle(null)}
        onBuy={handleDetailBuy}
        formatCurrency={formatCurrency}
      />

      {/* Cart drawer */}
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        shopId={shop.id}
        currency={currency}
      />
    </div>
  );
}

export function ShopView({ shop, articles }: ShopViewProps) {
  return (
    <CartProvider shopId={shop.id}>
      <ShopViewInner shop={shop} articles={articles} />
    </CartProvider>
  );
}
